import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { fmtMDHM } from '../../service/sopShared';
import { S, TabIntro, card, btnPrimary, sectionTitle, SEVERITY_DOT, dueText, actionLabel, fmtYMD } from './wsShared';
import { TAB_EXPLAIN } from '../../tour/tourSteps';

// ① 下一步 — "今天要为 TA 做的事": one card per next action (coloured dot,
// title, the SOP rule in grey, due / overdue, ONE button that does it), then
// the 备忘录 list. `nudge` (optional) renders above the cards — the 互相心动 · 去推荐 hints. `actions` comes from /workspace (or the client-side
// fallback); `onRun(action)` performs the card's button.

export default function NextStepTab({ actions = [], loading, isZh = true, svc, fallback, onRun, busyKey, memo, closed = false, closedAt = null, nudge = null }) {
  const t = (zh, en) => (isZh ? zh : en);
  const nextAt = svc && svc.next_due_at;
  return (
    <div>
      <TabIntro isZh={isZh} explain={isZh ? TAB_EXPLAIN.next : ''}
        text={t('从上往下做：红色最急，做完一件点它的按钮，这一条就会消失。', 'Work top to bottom: red first. Each card has one button that does it.')}
        steps={[
          t('看第一张卡片：标题是要做的事，灰字是 SOP 规定。', 'Read the first card: the title is the task, the grey line is the rule.'),
          t('点卡片右边的按钮，直接在这里完成（写记录 / 打勾 / 开报告）。', 'Press its button and finish it right here.'),
          t('答应会员的事记进下面的「备忘录」，到时间系统会提醒你。', 'Write promises into 备忘录 below — you will be reminded.'),
        ]} />

      {nudge}
      <h3 style={sectionTitle}>{t('今天要为 TA 做的事', 'To do for this member today')}</h3>
      {fallback && (
        <div style={{ fontSize: 12, color: S.mute, margin: '-4px 0 10px' }}>{t('（简版：完整的步骤检查部署后可用）', '(basic view — full step checks after the next deploy)')}</div>
      )}
      {loading ? (
        <div style={{ ...card, padding: 18, color: S.mute, fontSize: 13 }}>{t('加载中…', 'Loading…')}</div>
      ) : actions.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {actions.map((a, i) => {
            const dot = SEVERITY_DOT[a.severity] || SEVERITY_DOT.normal;
            const due = dueText(a.due_at, a.overdue, isZh);
            const late = a.overdue || (a.due_at && new Date(a.due_at).getTime() < Date.now());
            return (
              <div key={a.key || i} data-tour={i === 0 ? 'ws-next-first' : undefined} style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flex: '0 0 auto', alignSelf: 'flex-start', marginTop: 6 }} />
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: S.ink, lineHeight: 1.45 }}>{a.title}</div>
                  {a.why && <div style={{ fontSize: 12.5, color: S.sub, marginTop: 3, lineHeight: 1.6 }}>{a.why}</div>}
                  {due && <div style={{ fontSize: 12.5, marginTop: 4, color: late ? '#9E2B25' : S.mute, fontWeight: late ? 500 : 400 }}>{due}</div>}
                </div>
                <button type="button" data-tour={i === 0 ? 'ws-next-first-btn' : undefined} disabled={busyKey === a.key} onClick={() => onRun && onRun(a)} style={btnPrimary(busyKey === a.key)}>
                  {actionLabel(a, isZh)}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ ...card, padding: '22px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <CheckCircle2 size={22} color={S.ok} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: S.ink }}>
              {closed ? t(`已结单${closedAt ? ` · ${fmtYMD(closedAt)}` : ''}`, 'Service closed') : t('都完成了 ✓', 'All done ✓')}
            </div>
            <div style={{ fontSize: 13, color: S.sub, marginTop: 2 }}>
              {closed ? t('一对一满 3 个月，服务已完成，不需要再跟进。', 'The service is complete.')
                : nextAt ? t(`下次联系：${fmtMDHM(nextAt)}`, `Next contact: ${fmtMDHM(nextAt)}`) : t('有新的事情会出现在这里。', 'New tasks show up here.')}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 28 }} data-tour="ws-memo">
        <h3 style={sectionTitle}>{t('备忘录', 'Memo')}</h3>
        <div style={{ fontSize: 12.5, color: S.mute, margin: '-4px 0 6px' }}>{t('答应会员的事必须当天记进来：内容、时间；超时主管会跟进。', 'Log every promise the same day; overdue ones go to your supervisor.')}</div>
        <div style={{ ...card, padding: '6px 16px 14px' }}>{memo}</div>
      </div>
    </div>
  );
}
